import {
  OrderType,
  SearchLearningObjectiveStatementsDocument,
  useCreateLearningObjectiveStatementsMutation,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  Text,
  DialogActions,
  Button,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { FALLBACK_LEVEL_NAME } from '../../constants';
import {
  getIsNameAlreadyExistsError,
  getQuestionSourceForLOS,
  transformTextareaStringToArray,
} from '../../utils';
import { LOSInfoSection } from '../los-info-section';
import { LOSInput } from '../los-input';

import { type AddLOSFormValues, AddLOSFormInput } from './add-los-schema';

interface AddLOSFormModalProps {
  chapterId: string | null;
  open: boolean;
  onClose: () => void;
}

export default function AddLOSFormModal(props: Readonly<AddLOSFormModalProps>) {
  const { chapterId, open: isOpen, onClose } = props;

  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const formContext = useForm<AddLOSFormValues>({
    resolver: zodResolver(AddLOSFormInput),
  });
  const {
    formState: { isSubmitting },
    setError,
  } = formContext;

  const { qBankStructureInformation, qBankStructureLevelItems } = useQBankStructure();
  const { id: qBankStructureId, code } = qBankStructureInformation;

  const [createLOS] = useCreateLearningObjectiveStatementsMutation();

  const searchLOSVariables = useMemo(
    () => ({
      order: { field: 'updatedAt', type: OrderType.Asc },
      paging: { currentPage: 0, pageSize: 9999 },
      search: { qBankStructureId: qBankStructureId },
    }),
    [qBankStructureId],
  );

  const chapterName = useMemo(() => {
    if (!chapterId) return FALLBACK_LEVEL_NAME;
    const levelItem = qBankStructureLevelItems.find((item) => item.id === chapterId);
    return levelItem?.tag?.name ?? FALLBACK_LEVEL_NAME;
  }, [chapterId, qBankStructureLevelItems]);

  useEffect(() => {
    if (isOpen) {
      formContext.reset({ los: '' });
    }
  }, [isOpen]);

  const qSource = useMemo(() => {
    return getQuestionSourceForLOS(code, qBankStructureLevelItems, chapterId ?? '');
  }, [code, qBankStructureLevelItems, chapterId]);

  const handleClose = () => {
    onClose();
  };

  const handleResetForm = () => {
    formContext.reset();
  };

  const onSubmit = async (data: AddLOSFormValues) => {
    if (!chapterId) {
      logger.error('Chapter ID is not provided. Unable to create LOS.');
      showSnackbar({
        message: 'Unable to save LOS',
        variant: 'error',
      });
      return;
    }

    await createLOS({
      variables: {
        input: {
          qBankStructureId: qBankStructureId,
          chapterLevelItemId: chapterId,
          names: transformTextareaStringToArray(data.los),
        },
      },
      refetchQueries: [
        {
          query: SearchLearningObjectiveStatementsDocument,
          variables: searchLOSVariables,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        handleClose();
        showSnackbar({
          message: 'LOS has been added',
          variant: 'success',
        });
      },
      onError: (error) => {
        if (getIsNameAlreadyExistsError(error)) {
          setError('los', { message: 'ข้อมูลซ้ำกับวัตถุประสงค์การเรียนรู้ที่มีอยู่แล้วในบทนี้' });
        } else {
          showSnackbar({
            message: 'Unable to add LOS',
            variant: 'error',
          });
        }
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='lg'
      slotProps={{
        paper: {
          component: Form,
          onSubmit: formContext.handleSubmit(onSubmit),
          sx: { maxHeight: '775px' },
        },
        transition: {
          onExited: handleResetForm,
        },
      }}
    >
      <FormProvider {...formContext}>
        <DialogTitle onClose={handleClose} loading={isSubmitting}>
          Add LOS
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={(theme) => ({ display: 'flex', flexDirection: 'column', gap: theme.spacing(6) })}
        >
          <Text variant='body1Semibold'>กำหนดวัตถุประสงค์การเรียนรู้</Text>
          <LOSInfoSection name={chapterName} qSource={qSource} />
          <LOSInput
            name='los'
            label='เพิ่มวัตถุประสงค์การเรียนรู้'
            helperText='สามารถเพิ่มวัตถุประสงค์การเรียนรู้พร้อมกันได้หลายข้อโดยการเว้นบรรทัด'
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            disabled={isSubmitting}
            onClick={handleClose}
            color='secondary'
            variant='outlined'
            size='large'
          >
            Cancel
          </Button>
          <Button type='submit' size='large' loading={isSubmitting}>
            Add LOS
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
